import {ComponentType, FC, ReactNode} from 'react';

import {createView, view, type View} from '@sail/ui';

import {Conversation} from '@stripe-internal/conversations-client';
import {ConversationActivityIndicatorProvider} from '@stripe-internal/conversations-ui';

import {AssistantInput} from './AssistantInput';
import {AssistantLog} from './AssistantLog';
import {CloseConfirmationModal} from './CloseConfirmationModal';
import {ComponentWithOverride} from './ComponentWithOverride';
import {useConversationExceptions} from '../hooks/useConversationExceptions';
import {useConversationInlineDataEvents} from '../hooks/useConversationInlineDataEvents';
import {useVisibility} from '../hooks/appHooks';

type Props = View.IntrinsicElement<
  'div',
  {
    conversation: Conversation;
    log?: ReactNode | ComponentType<{conversation: Conversation}>;
    input?: ReactNode | ComponentType<{conversation: Conversation}>;
  }
>;

export const ConversationScreen: FC<View.ViewProps<Props>> = createView<Props>(
  ({conversation, log, input, ...props}) => {
    useConversationExceptions(conversation);
    useConversationInlineDataEvents(conversation);
    const {visibility} = useVisibility();

    return (
      <view.div
        data-testid="conversation-screen"
        css={{
          display: 'flex',
          flexDirection: 'column',
          flex: 1,
          flexBasis: 'auto',
        }}
        // Setting the `aria-live` attribute so that screen readers can read new messages coming in.
        // We set it to `polite` so that new message don't interrupt the reading of previous messages.
        aria-live="polite"
        {...props}
      >
        <ConversationActivityIndicatorProvider>
          {/*
            if a custom onClose handler used handles the onClose callback,
            the default close confirmation behavior will be skipped
          */}
          <CloseConfirmationModal />

          <ComponentWithOverride<{conversation: Conversation}>
            override={log}
            component={AssistantLog}
            conversation={conversation}
          />
          <ComponentWithOverride<{
            conversation: Conversation;
            focusInput?: boolean;
          }>
            override={input}
            component={AssistantInput}
            conversation={conversation}
            focusInput={visibility === 'open'}
          />
        </ConversationActivityIndicatorProvider>
      </view.div>
    );
  },
);
